import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { staggerProps } from '../../lib/stagger'
import { relativeTime } from '../../lib/orgAccess'
import type { OrgJoinRequestRow } from '../../api/endpoints'

/**
 * JoinRequestQueue — the OWNER'S half of "I work at a company that's already
 * here" (docs/ORG-FRONTEND-PLAN.md §1b).
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────
 * `POST /org/join-requests` shipped with a screen; `GET /org/join-requests`,
 * `…/approve` and `…/decline` shipped with none, so `orgApi.joinRequests`,
 * `approveJoinRequest` and `declineJoinRequest` had ZERO non-test callers. An
 * employee could ask to join and no human on the platform had a way to say yes.
 * Tej's own acceptance test — tej@tejjogani.com joining Tejjogani Diamonds —
 * ended in a queue nobody could open.
 *
 * ── THE EVIDENCE IS THE PRODUCT ───────────────────────────────────────────
 * Approving hands over the company's KYC: live prices, supplier contact
 * details, `refStockId` and the live-Stripe checkout gate (CLAUDE.md invariant
 * 3). So the row leads with WHY we think this person belongs, and an
 * `company_name` match — which anyone can type — says exactly that rather than
 * being dressed up as proof. That is the same distinction the notification
 * email draws, and the two must not disagree.
 *
 * ── AND THE THREE STATES THAT ARE NOT "A LIST OF PEOPLE" ──────────────────
 *  • `queueUnavailable` — the QUERY failed. The server refuses to let a broken
 *    index look like "nobody has asked", and so does this: an honest failure
 *    with a retry, never an empty state.
 *  • EMPTY — rendered as nothing at all. A permanent "no requests" card is a
 *    nag on a screen whose job is the roster.
 *  • AT THE SEAT LIMIT — approving 402s. `onSeatLimit` hands the whole `seats`
 *    block to the same paywall the 4th invite opens, and the request stays
 *    PENDING server-side, so the owner buys a seat and presses Approve again.
 *    Nothing is burned by hitting the wall.
 */

export type JoinRowBusy = null | { uid: string; kind: 'approve' | 'decline' }

export function JoinRequestQueue({
  requests,
  unavailable,
  busy,
  errors,
  onApprove,
  onDecline,
  onRetry,
}: {
  requests: OrgJoinRequestRow[]
  /** The queue read failed — NOT the same thing as an empty queue. */
  unavailable: boolean
  busy: JoinRowBusy
  /** Per-uid server messages, rendered in the row that produced them. */
  errors: Record<string, string>
  onApprove: (uid: string) => void
  onDecline: (uid: string, reason: string) => void
  onRetry: () => void
}) {
  const now = Date.now()

  if (unavailable) {
    return (
      <div
        className="mt-5 rounded-md border border-warning/40 bg-warning/[0.07] p-3"
        data-testid="join-queue-unavailable"
      >
        <p className="text-[13px] font-semibold text-ink">We couldn’t load join requests.</p>
        <p className="mt-1 text-[13px] leading-relaxed text-soft">
          Somebody may be waiting on you — this is a failure to read the list, not an empty list.
          Everything else on this screen is fine.
        </p>
        <PressScale
          feel="tap"
          onClick={onRetry}
          className="btn-secondary mt-2.5 h-11 px-4 text-[13px]"
          data-testid="join-queue-retry"
        >
          Try again
        </PressScale>
      </div>
    )
  }

  // Nothing to answer ⇒ nothing on screen. The roster is the point of the page.
  if (requests.length === 0) return null

  return (
    <section className="mt-6" data-testid="join-queue" aria-labelledby="join-queue-heading">
      <h2
        id="join-queue-heading"
        className="text-[11px] font-semibold uppercase tracking-[0.14em] text-soft"
      >
        {requests.length === 1 ? 'Someone wants to join' : `${requests.length} people want to join`}
      </h2>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-soft">
        Approving uses one seat and gives them your company’s verification — live prices and
        supplier contact details. Nothing happens until you decide.
      </p>
      <ul role="list" className="mt-3 divide-y divide-line">
        {requests.map((r, i) => (
          <JoinRequestRow
            key={r.uid}
            {...staggerProps(i)}
            request={r}
            now={now}
            busy={busy}
            error={errors[r.uid] ?? null}
            onApprove={() => onApprove(r.uid)}
            onDecline={(reason) => onDecline(r.uid, reason)}
          />
        ))}
      </ul>
    </section>
  )
}

/** How we came to offer this org, in words the approver can act on. */
function evidence(r: OrgJoinRequestRow): { title: string; blurb: string; strong: boolean } {
  if (r.matchBasis === 'email_domain') {
    return {
      strong: true,
      title: 'Their email is on your domain',
      blurb: 'The strongest signal we have — but it is a signal, not a verification. Only you know who works with you.',
    }
  }
  return {
    strong: false,
    title: 'They typed your company name',
    blurb: 'Anyone can type a company name, so we deliberately don’t treat this as proof. Recognise the person before you approve.',
  }
}

function JoinRequestRow({
  request, now, busy, error, onApprove, onDecline, className = '', style,
}: {
  request: OrgJoinRequestRow
  now: number
  busy: JoinRowBusy
  error: string | null
  onApprove: () => void
  onDecline: (reason: string) => void
  className?: string
  style?: React.CSSProperties
}) {
  const reduce = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  const label = request.displayName || request.email || request.uid
  const ev = evidence(request)
  const mine = busy?.uid === request.uid
  const asked = relativeTime(request.createdAt, now)
  const lapses = relativeTime(request.expiresAt, now)

  return (
    <li className={`relative ${className}`.trim()} style={style}>
      <PressScale
        as="div"
        feel={false}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`join-actions-${request.uid}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v) }
        }}
        className="flex min-h-[64px] w-full cursor-pointer items-center gap-3 px-1 py-3 text-left"
        data-testid={`join-request-${request.uid}`}
      >
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold-500/45 text-[12px] font-bold text-gold-600"
        >
          ?
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-medium text-ink">{label}</span>
          {/* The BASIS, on the row itself. An approver who has to expand to
              find out why we matched them will approve without finding out. */}
          <span
            className={`mt-0.5 block truncate text-[12.5px] ${ev.strong ? 'text-soft' : 'text-warning'}`}
            data-testid={`join-basis-${request.uid}`}
          >
            {ev.title}{asked ? ` · asked ${asked}` : ''}
          </span>
        </span>
        <span className="shrink-0 rounded-full border border-gold-500/45 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-gold-600">
          Asking
        </span>
      </PressScale>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`join-actions-${request.uid}`}
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0.12 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 px-1 pb-4">
              {request.email && <p className="truncate text-[12.5px] text-soft">{request.email}</p>}
              <p className="text-[12.5px] leading-relaxed text-soft">
                <span className="font-semibold text-ink">{ev.title}.</span> {ev.blurb}
                {lapses ? ` The request lapses on its own ${lapses}.` : ''}
              </p>

              {error && (
                <p
                  className="text-[13px] font-medium text-danger"
                  role="alert"
                  data-testid={`join-error-${request.uid}`}
                >
                  {error}
                </p>
              )}

              {!declining && (
                <div className="flex flex-wrap gap-2">
                  <PressScale
                    feel="tap"
                    disabled={!!busy}
                    onClick={() => { feedback.select(); onApprove() }}
                    className="btn-primary h-11 px-4 text-[13px]"
                    data-testid={`join-approve-${request.uid}`}
                  >
                    {mine && busy?.kind === 'approve' ? 'Approving…' : `Approve ${label}`}
                  </PressScale>
                  <PressScale
                    feel="dim"
                    disabled={!!busy}
                    onClick={() => setDeclining(true)}
                    className="inline-flex h-11 items-center rounded-full border border-danger/40 px-4 text-[13px] font-semibold text-danger"
                    data-testid={`join-decline-${request.uid}`}
                  >
                    Decline
                  </PressScale>
                </div>
              )}

              {declining && (
                <div className="rounded-md border border-danger/30 bg-danger/[0.04] p-3">
                  <p className="text-[13px] leading-relaxed text-ink">
                    {label} keeps their own account and never sees anything of yours. They’re told
                    it wasn’t approved — you can say why, and they’ll read it.
                  </p>
                  <label className="mt-2.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-soft" htmlFor={`join-reason-${request.uid}`}>
                    Reason (optional)
                  </label>
                  <input
                    id={`join-reason-${request.uid}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. we don’t know this person"
                    className="mt-1 h-11 w-full rounded-md border border-line bg-paper px-3 text-[14px] text-ink placeholder:text-soft/70"
                    data-testid={`join-reason-${request.uid}`}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <PressScale
                      feel="tap"
                      onClick={() => { setDeclining(false); setReason('') }}
                      className="btn-secondary h-11 px-4 text-[13px]"
                      data-testid={`join-decline-cancel-${request.uid}`}
                    >
                      Cancel
                    </PressScale>
                    <PressScale
                      feel="deny"
                      disabled={!!busy}
                      onClick={() => onDecline(reason)}
                      className="inline-flex h-11 items-center justify-center rounded-full bg-danger px-4 text-[13px] font-semibold text-white disabled:opacity-50"
                      data-testid={`join-decline-confirm-${request.uid}`}
                    >
                      {mine && busy?.kind === 'decline' ? 'Declining…' : `Decline ${label}`}
                    </PressScale>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  )
}
